/* ============================================================================
   /login — Build 9 reskin.
   Design system: Surface raised card on surface-base page. Matches the
   rest of the app (Linear-style auth, not Discord/neon). Same primitives
   as every other screen.
   ============================================================================ */
import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { useAuth } from "@/components/AuthProvider";
import { authErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/NexButton";
import { TextField } from "@/components/ui/NexTextField";
import { Logo } from "@/components/Logo";
import { NativeOAuthButtons } from "@/components/auth/NativeOAuthButtons";

export default function Login() {
  const { signin, isSignedIn } = useAuth();
  const [, setLocation] = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isSignedIn) setLocation("/");
  }, [isSignedIn, setLocation]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signin(email.trim(), password);
      setLocation("/");
    } catch (err: unknown) {
      setError(authErrorMessage(err));
      setBusy(false);
    }
  };

  return (
    <div
      className="relative min-h-[100dvh] bg-[var(--surface-base)] flex justify-center px-4 sm:items-center"
      style={{
        paddingTop: "calc(var(--nx-safe-top) + 1.25rem)",
        paddingBottom: "calc(var(--nx-safe-bottom) + 1rem)",
      }}
    >
      <div
        className="relative w-full max-w-sm bg-[var(--surface-raised)] border border-[var(--border-subtle)] rounded-[var(--radius-lg)] shadow-[var(--shadow-md)] p-7"
        data-testid="card-login"
      >
        {/* Logo */}
        <div className="flex justify-center mb-5">
          <div style={{ width: "min(72px, 30vw)" }}>
            <Logo size={72} />
          </div>
        </div>

        {/* Eyebrow + title */}
        <p className="text-center text-xs font-medium text-[var(--text-tertiary)] tracking-wide uppercase mb-1">
          Welcome back
        </p>
        <h1
          className="text-2xl font-bold text-center text-[var(--text-primary)] leading-tight mb-2"
          data-testid="text-login-title"
        >
          Sign in to your Dex
        </h1>
        <p className="text-sm text-center text-[var(--text-secondary)] mb-6 leading-relaxed">
          Sync your collection across phone and the web portal.
        </p>

        <form onSubmit={submit} className="space-y-4">
          <TextField
            label="Email"
            id="login-email"
            data-testid="input-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <TextField
            label="Password"
            id="login-password"
            data-testid="input-password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            error={error ?? undefined}
          />

          <div className="text-right -mt-2">
            <Link
              href="/auth/forgot-password"
              className="text-xs text-[var(--text-tertiary)] hover:text-[var(--accent-fill)] transition-colors no-underline"
              data-testid="link-forgot-password"
            >
              Forgot your password?
            </Link>
          </div>

          <Button
            type="submit"
            variant="primary"
            size="lg"
            disabled={busy}
            className="w-full"
            data-testid="button-login-submit"
          >
            {busy ? "Signing in…" : "Sign in"}
          </Button>
        </form>

        {/* Divider */}
        <div className="flex items-center gap-3 my-5">
          <div className="flex-1 border-t border-[var(--border-subtle)]" />
          <span className="text-[10px] uppercase tracking-widest font-semibold text-[var(--text-tertiary)]">
            or
          </span>
          <div className="flex-1 border-t border-[var(--border-subtle)]" />
        </div>

        {/* Email / password */}
        {/* OAuth, below the email form (2026-09-11): the email and password
            fields sit high enough to stay clear of the keyboard, whichever
            element owns the page's scroll. */}
        <NativeOAuthButtons onSuccess={() => setLocation("/")} />

        <p className="text-xs mt-5 text-center text-[var(--text-tertiary)]">
          New here?{" "}
          <Link
            href="/signup"
            className="font-semibold text-[var(--accent-fill)] no-underline hover:opacity-80"
            data-testid="link-signup"
          >
            Get started
          </Link>
        </p>
      </div>
    </div>
  );
}
